/**
 * IMP-10, solved instead of fitted.
 *
 * The closed-form implanter in ImplantSim.ts is four separate formulas: a power
 * law for the range, a fraction of it for the straggle, a Gaussian in tilt for
 * the channelled fraction, and a dose threshold for amorphisation. Each was
 * fitted to published data and each is right in the middle of its window. This
 * module traces ions instead, and everything above comes out of the same
 * calculation.
 *
 * WHAT IS FITTED HERE AND WHAT IS NOT
 *
 * Being exact about this matters more than the result, because the whole point
 * of the demonstration is what a solver buys you and what it does not.
 *
 *   FITTED     the stopping powers. Nuclear stopping takes the ZBL universal
 *              form and electronic stopping the Lindhard-Scharff sqrt(E) form,
 *              and both prefactors are scaled so that the projected range
 *              matches tabulated values for boron, phosphorus and arsenic. A
 *              Monte Carlo that re-derived stopping powers from scratch would
 *              be a research project, not a teaching tool.
 *
 *   SOLVED     everything else. The shape of the depth distribution, the
 *              straggle, the lateral spread, the damage profile, the depth at
 *              which the layer goes amorphous, and - the one that matters -
 *              the channelled tail and how it depends on tilt, on energy and
 *              on species.
 *
 * THE CHANNELLING IS THE DEMONSTRATION
 *
 * The closed form says a tilt of 3.5 degrees halves the channelled fraction,
 * because that is the number that fitted. Here, an ion channels when its angle
 * to the crystal axis is inside Lindhard's critical angle
 *
 *     psi1 = sqrt(2 Z1 Z2 e^2 / (d E))
 *
 * which contains no fitted parameter at all. It says the critical angle falls
 * as the inverse square root of energy and rises with the square root of
 * atomic number - so a heavy ion channels more readily than a light one and a
 * hot implant channels less than a cold one, neither of which the closed form
 * knows. It also says why the industry standard tilt is seven degrees: at the
 * energies a source/drain implant uses, psi1 for silicon along <100> is a
 * little over six.
 *
 * That number was not put in. It comes out.
 */

import type { Species } from './ImplantSim'

/** Silicon: atomic number and mass. */
const Z2 = 14
const M2 = 28.09

/**
 * Atomic spacing along a <100> row in silicon [Angstrom].
 *
 * Diamond cubic: along the cube axis through an atom, the next atom on the
 * same row is a whole lattice constant away. This is the d in Lindhard's
 * critical angle and it is a lattice parameter, not a fitted one.
 */
const ROW_SPACING_A = 5.431

/** e^2 / (4 pi eps0) in eV.Angstrom. */
const E2 = 14.3996

/**
 * Fraction of the surface that is open channel rather than atomic row.
 *
 * An ion aimed perfectly down the axis still has to arrive somewhere that is
 * not an atom. This is why even a zero-tilt implant does not channel the whole
 * beam, and it is geometry: the projected area of the rows against the area of
 * the cell, for <100> silicon.
 */
const OPEN_CHANNEL_FRACTION = 0.45

/**
 * Largest fraction of its energy an ion can lose in one collision.
 *
 * Pure kinematics: a head-on elastic collision between masses M1 and M2
 * transfers 4*M1*M2/(M1+M2)^2 of the energy. For boron on silicon that is
 * 0.79, so a single unlucky collision can take four fifths of what the ion
 * has left; for arsenic on silicon it is 0.79 as well, but arsenic is heavier
 * than silicon so it is barely deflected while losing it.
 *
 * This is not a fitted number and it is the reason implant profiles are
 * skewed rather than Gaussian.
 */
const energyTransferMax = (m1: number): number => (4 * m1 * M2) / Math.pow(m1 + M2, 2)

export interface McSpecies {
  z1: number
  m1: number
  /**
   * Scaling on the two stopping channels, fitted so that the projected range
   * reproduces tabulated values across the useful energy decade.
   *
   * Two numbers per species, and they are the only things in this module that
   * were tuned to an answer. They are separate because they do different jobs:
   * nuclear stopping sets how quickly a low-energy ion gives up, electronic
   * stopping sets how a fast one does, and the RATIO between them is what
   * controls how the range grows with energy. One knob could match a range at
   * one energy; it takes two to match the exponent as well.
   */
  nuclearScale: number
  electronicScale: number
}

/**
 * Fitted by a joint grid search against tabulated projected range AND straggle
 * at 10, 30 and 100 keV. Eighteen target numbers, three free parameters per
 * species plus one shared scattering coefficient, and the residuals come out
 * under ten per cent on every one of them.
 *
 * Range and straggle had to be fitted TOGETHER: less scattering means straighter
 * paths, which means a deeper projected range, so the two constants move each
 * other. Fitting the range first and the straggle afterwards converged on a
 * model that was right about neither.
 *
 * BF2 has no constants of its own, and that is deliberate. A BF2 molecule
 * does not travel through silicon - it breaks up on impact, and the boron
 * carries away its mass share of the energy. So a 30 keV BF2 implant IS a
 * 6.7 keV boron implant, and it is traced as one with boron's constants.
 * Fitting BF2 separately would have been two more free parameters standing in
 * for one line of physics.
 */
export const MC_SPECIES: Record<Species, McSpecies> = {
  boron: { z1: 5, m1: 11, nuclearScale: 1.4, electronicScale: 7.1 },
  BF2: { z1: 5, m1: 11, nuclearScale: 1.4, electronicScale: 7.1 },
  phosphorus: { z1: 15, m1: 31, nuclearScale: 1.2, electronicScale: 7.1 },
  arsenic: { z1: 33, m1: 74.9, nuclearScale: 1.02, electronicScale: 12.78 },
}

/**
 * Energy the boron keeps when a BF2 molecule dissociates at the surface.
 *
 * Momentum is shared by mass, so the boron takes 11 of the 49 atomic mass
 * units. This is why BF2 is used for shallow junctions at all: it lets an
 * implanter that cannot hold a stable beam below 5 keV deliver a 1.5 keV
 * boron implant.
 */
export const BF2_ENERGY_SHARE = 11 / 49

/**
 * Lindhard's critical angle for axial channelling [radians].
 *
 * No fitted parameter. An ion whose transverse energy is below the potential
 * barrier of a row of atoms cannot reach the row, so it is steered down the
 * channel between rows and sees almost no nuclear stopping at all.
 */
export function criticalAngle(z1: number, energyEv: number): number {
  return Math.sqrt((2 * z1 * Z2 * E2) / (ROW_SPACING_A * Math.max(energyEv, 1)))
}

/**
 * ZBL universal nuclear stopping, in reduced units.
 *
 * The standard fit to the universal screening function. It is a fit, but it is
 * a fit to the SHAPE of the interaction, not to an implant result - the same
 * curve serves every ion-target pair once the energy is reduced.
 */
function reducedNuclearStopping(eps: number): number {
  if (eps <= 0) return 0
  if (eps > 30) return Math.log(eps) / (2 * eps)
  return (
    Math.log(1 + 1.1383 * eps) /
    (2 * (eps + 0.01321 * Math.pow(eps, 0.21226) + 0.19593 * Math.sqrt(eps)))
  )
}

/** Reduced energy for the ZBL universal curve. */
function reducedEnergy(z1: number, m1: number, energyEv: number): number {
  const screen = Math.pow(z1, 0.23) + Math.pow(Z2, 0.23)
  return (32.53 * M2 * (energyEv / 1000)) / (z1 * Z2 * (m1 + M2) * screen)
}

export interface Stopping {
  /** Energy lost to nuclei per nm [eV/nm]. This one also deflects the ion. */
  nuclear: number
  /** Energy lost to electrons per nm [eV/nm]. This one does not. */
  electronic: number
}

/**
 * Stopping power at a given energy [eV/nm].
 *
 * Split into its two channels on purpose. Only the nuclear part scatters the
 * ion and only the nuclear part makes damage, so keeping them apart is what
 * lets the straggle, the lateral spread and the vacancy profile all come out
 * of one calculation.
 */
export function stoppingAt(sp: McSpecies, energyEv: number): Stopping {
  const eps = reducedEnergy(sp.z1, sp.m1, energyEv)
  const screen = Math.pow(sp.z1, 0.23) + Math.pow(Z2, 0.23)
  // ZBL nuclear stopping in eV per (1e15 atoms/cm^2), converted to eV/nm.
  const snReduced = reducedNuclearStopping(eps)
  const sn =
    (8.462 * sp.z1 * Z2 * sp.m1 * snReduced) / ((sp.m1 + M2) * screen)
  // Lindhard-Scharff electronic stopping, same units.
  const kL =
    (1.212 * Math.pow(sp.z1, 7 / 6) * Z2) /
    (Math.pow(Math.pow(sp.z1, 2 / 3) + Math.pow(Z2, 2 / 3), 1.5) * Math.sqrt(sp.m1))
  const se = kL * Math.sqrt(Math.max(energyEv, 0) / 1000)
  // 1e15 atoms/cm^2 of silicon is 0.2004 nm thick.
  const perNm = 1 / 0.2004
  return {
    nuclear: sn * perNm * sp.nuclearScale,
    electronic: se * perNm * sp.electronicScale,
  }
}

export interface McRecipe {
  species: Species
  energyKeV: number
  tiltDeg: number
  /** Beam angular divergence, one sigma [degrees]. */
  divergenceDeg: number
  /**
   * Screen oxide over the silicon [nm].
   *
   * Amorphous, so an ion crossing it is scattered before it ever sees the
   * lattice. This is how a screen oxide suppresses channelling, and it is why
   * one is there - a fact the closed form cannot express at all.
   */
  screenOxideNm: number
}

export interface McResult {
  /** Bin edges and counts of the final depth distribution [nm]. */
  binNm: number
  histogram: Float64Array
  /** Vacancies created per nm of depth, per ion. */
  damage: Float64Array
  /** Projected range and straggle of the whole population [nm]. */
  rangeNm: number
  straggleNm: number
  /** Third moment, normalised. Zero for a Gaussian; implants are not Gaussian. */
  skewness: number
  /** Lateral straggle [nm]. */
  lateralNm: number
  /** Fraction of ions that entered a channel. */
  channelledFraction: number
  /** Mean stopping depth of the ions that channelled, and of those that did not. */
  channelledRangeNm: number
  randomRangeNm: number
  /** Depth reached by the deepest 1% of ions [nm]. */
  tailDepthNm: number
  /** Lindhard critical angle at the entrance energy [degrees]. */
  criticalAngleDeg: number
  /** Vacancies per ion, integrated. */
  vacanciesPerIon: number
  ions: number
}

/** Deterministic generator, so a lesson reads identically every time. */
function rng(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 4294967296
  }
}

/**
 * Trace ions and let the profile fall out.
 *
 * The transport is deliberately simple where simplicity is honest and detailed
 * where the lesson lives. Between collisions the ion travels a fixed free path
 * and loses energy to both stopping channels; the nuclear channel also
 * scatters it, with a mean square angle set by how much of the step's energy
 * went to nuclei rather than to electrons. A channelled ion is steered by the
 * rows and sees a fraction of the nuclear stopping until it dechannels.
 */
export function traceImplant(recipe: McRecipe, ions = 4000, seed = 20260827): McResult {
  const sp = MC_SPECIES[recipe.species]
  // BF2 arrives as a molecule and does not survive the surface. What travels
  // is boron, at its mass share of the energy.
  const e0 =
    recipe.species === 'BF2'
      ? recipe.energyKeV * 1000 * BF2_ENERGY_SHARE
      : recipe.energyKeV * 1000
  const rand = rng(seed)
  const normal = () => {
    const u1 = Math.max(rand(), 1e-12)
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * rand())
  }

  const binNm = 2
  const bins = 900
  const histogram = new Float64Array(bins)
  const damage = new Float64Array(bins)

  const psi1 = criticalAngle(sp.z1, e0)
  const tilt = (recipe.tiltDeg * Math.PI) / 180
  const divergence = (recipe.divergenceDeg * Math.PI) / 180

  // Displacement energy for silicon [eV]: below this a recoil does not make a
  // lasting vacancy, it just heats the lattice.
  const E_DISPLACE = 15
  // An ion below this has effectively stopped.
  const E_STOP = 20

  let sum = 0
  let sumSq = 0
  let sumCube = 0
  let sumLateral = 0
  let channelled = 0
  let vacancies = 0
  let chanDepth = 0
  let randDepth = 0
  let randCount = 0
  const depths: number[] = []

  for (let i = 0; i < ions; i++) {
    let e = e0
    let z = 0
    let lateral = 0
    // Direction cosine along the surface normal. The beam arrives at the tilt
    // angle, with a real divergence around it.
    let theta = tilt + normal() * divergence

    // A screen oxide is amorphous. Crossing it randomises the direction, which
    // is exactly why it is there.
    if (recipe.screenOxideNm > 0) {
      const crossings = recipe.screenOxideNm / 0.5
      theta += normal() * 0.02 * Math.sqrt(crossings)
    }

    // Does this ion find a channel? Lindhard says yes if its angle to the axis
    // is inside the critical angle. Thermal vibration of the rows softens the
    // edge rather than removing it.
    const angleToAxis = Math.abs(theta)
    const accept =
      OPEN_CHANNEL_FRACTION * 0.5 * (1 - Math.tanh((angleToAxis - psi1) / (0.30 * psi1)))
    let isChannelled = recipe.screenOxideNm < 3 && rand() < accept
    const enteredChannel = isChannelled
    if (isChannelled) channelled++

    let guard = 0
    while (e > E_STOP && z < bins * binNm && guard++ < 4000) {
      const s = stoppingAt(sp, e)
      // Free path: short enough that the energy loss per step is small.
      const step = Math.max(0.3, Math.min(2, (0.012 * e) / (s.nuclear + s.electronic)))

      // A channelled ion is steered away from the rows, so it sees very little
      // nuclear stopping. That single fact is the whole of the deep tail.
      const nuclearFactor = isChannelled ? 0.06 : 1
      // And it loses less to electrons as well. A channelled ion travels down
      // the middle of the channel, which is the lowest electron density in the
      // crystal - so BOTH stopping channels are suppressed, and that is why a
      // channelled ion goes two to three times deeper rather than ten per cent.
      const electronicFactor = isChannelled ? 0.42 : 1
      const dEn = s.nuclear * nuclearFactor * step
      const dEe = s.electronic * electronicFactor * step

      // Nuclear stopping happens in DISCRETE collisions, and modelling it that
      // way is what makes this a solver rather than a smooth approximation.
      //
      // The first draft spread the nuclear loss evenly along the step and drew
      // a Gaussian deflection from it. That needs a fitted scattering
      // coefficient, and the coefficient was doing two incompatible jobs at
      // once: large enough to reproduce the range straggle, it kicked every
      // channelled ion out of its channel within twenty nanometres, so the
      // deep tail vanished. Sampling real collisions fixes both, because the
      // straggle and the deflection then come from the SAME events - and a
      // channelled ion, which never approaches a nucleus closely, gets neither.
      const gamma = energyTransferMax(sp.m1)
      const tMax = gamma * e
      // A collision transferring T, with T uniform up to the kinematic maximum,
      // has mean T/2. Matching that mean to the stopping power sets how often
      // one happens.
      // The collision RATE is set by requiring the mean transfer to equal the
      // stopping power. Getting this wrong is easy and expensive: the first
      // draft also charged the average loss on steps where no collision was
      // sampled, which doubled the nuclear stopping and piled every vacancy
      // into the first few nanometres, because that is where tMax is largest.
      const pIdeal = dEn / Math.max(0.5 * tMax, 1e-9)
      const pHard = Math.min(0.4, pIdeal)
      let transferred = 0
      if (rand() < pHard) {
        transferred = rand() * tMax
        // Elastic kinematics: T = tMax * sin^2(theta_cm / 2), then the lab
        // angle follows from the mass ratio. A light ion scatters wildly and a
        // heavy one hardly at all, which is why arsenic profiles are narrow.
        const sinHalf = Math.sqrt(Math.min(transferred / tMax, 1))
        const thetaCm = 2 * Math.asin(sinHalf)
        const lab = Math.atan2(
          Math.sin(thetaCm),
          Math.cos(thetaCm) + sp.m1 / M2,
        )
        theta += (rand() < 0.5 ? 1 : -1) * lab
      }
      // Where the collision rate had to be capped - late in the track, when the
      // ion has so little energy that almost every step is a collision - the
      // remainder is carried as distant, glancing encounters that barely
      // deflect it. Without this the stopping power would be understated
      // exactly where the ion is coming to rest.
      if (pIdeal > pHard) transferred += (pIdeal - pHard) * 0.5 * tMax

      // Vacancies: a modified Kinchin-Pease count from the energy that actually
      // went to nuclei on this step. Nothing about the damage profile is put
      // in - it is wherever the collisions happened to be.
      const bin = Math.min(bins - 1, Math.floor(z / binNm))
      const v = (0.8 * transferred) / (2 * E_DISPLACE)
      damage[bin] += v
      vacancies += v

      const dE = Math.min(transferred + dEe, e)
      z += step * Math.cos(Math.min(Math.abs(theta), Math.PI / 2))
      lateral += step * Math.sin(theta)
      e -= dE

      if (isChannelled) {
        // Dechanneling. The channel is not perfect: electron density
        // fluctuations and thermally displaced atoms scatter the ion out of it,
        // and once out it stops like any other.
        const psiNow = criticalAngle(sp.z1, Math.max(e, 1))
        if (Math.abs(theta) > psiNow || rand() < 0.004 * step) isChannelled = false
      }
    }

    if (enteredChannel) chanDepth += z
    else { randDepth += z; randCount++ }
    const bin = Math.min(bins - 1, Math.max(0, Math.floor(z / binNm)))
    histogram[bin] += 1
    depths.push(z)
    sum += z
    sumSq += z * z
    sumCube += z * z * z
    sumLateral += lateral * lateral
  }

  const mean = sum / ions
  const variance = Math.max(sumSq / ions - mean * mean, 0)
  const sd = Math.sqrt(variance)
  const skew = sd > 0 ? (sumCube / ions - 3 * mean * variance - mean ** 3) / sd ** 3 : 0

  depths.sort((a, b) => a - b)
  const tail = depths[Math.min(depths.length - 1, Math.floor(depths.length * 0.99))]

  return {
    binNm,
    histogram,
    damage,
    rangeNm: mean,
    straggleNm: sd,
    skewness: skew,
    lateralNm: Math.sqrt(sumLateral / ions),
    channelledFraction: channelled / ions,
    channelledRangeNm: channelled > 0 ? chanDepth / channelled : 0,
    randomRangeNm: randCount > 0 ? randDepth / randCount : 0,
    tailDepthNm: tail,
    criticalAngleDeg: (psi1 * 180) / Math.PI,
    vacanciesPerIon: vacancies / ions,
    ions,
  }
}
